import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { api } from "../lib/api";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const polls = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }

    let active = true;
    const poll = async () => {
      if (!active) return;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") { setData(res.data); setState("paid"); return; }
        if (res.data.status === "expired" || res.data.payment_status === "failed") { setState("error"); return; }
      } catch {
        setState("error"); return;
      }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, []);

  return (
    <div className="bg-paper min-h-screen flex flex-col" data-testid="payment-success-page">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center py-24">
          {state === "checking" && (
            <>
              <Loader2 size={44} className="text-trailride animate-spin mx-auto mb-6" />
              <h1 className="font-serif text-3xl font-bold text-ink">Confirming your payment…</h1>
              <p className="font-sans text-ink/60 mt-3">One moment while we activate your membership.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <CheckCircle2 size={52} className="text-trailride mx-auto mb-6" />
              <h1 className="font-serif text-4xl font-bold text-ink">You're an active member.</h1>
              <p className="font-sans text-ink/70 mt-4">
                Dues paid{data?.donation_amount > 0 ? ` · thank you for your $${Number(data.donation_amount).toFixed(2)} donation` : ""}. Welcome to the ride.
              </p>
              <button onClick={() => navigate("/member-dashboard")} data-testid="success-dashboard" className="inline-flex items-center gap-2 mt-8 px-8 py-4 rounded-full bg-trailride text-white font-sans font-medium hover:bg-trailride-dark transition-colors">
                Go to my dashboard
              </button>
            </>
          )}
          {(state === "timeout" || state === "error") && (
            <>
              <XCircle size={48} className="text-ink/40 mx-auto mb-6" />
              <h1 className="font-serif text-3xl font-bold text-ink">{state === "timeout" ? "Still processing…" : "We couldn't confirm that."}</h1>
              <p className="font-sans text-ink/60 mt-3">
                {state === "timeout"
                  ? "Your payment may take a moment to finalize. Check your dashboard shortly."
                  : "If you were charged, your membership will update automatically. Please check your dashboard."}
              </p>
              <button onClick={() => navigate("/member-dashboard")} className="inline-flex items-center gap-2 mt-8 px-8 py-4 rounded-full border border-ink/20 text-ink font-sans font-medium hover:border-trailride hover:text-trailride transition-colors">
                Go to my dashboard
              </button>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
